import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useCapChartPageHeaderController as useCapChartSettingsController } from '@dealstack/frontend/app/pages/Project/Cap/CapChartPage/useCapChartPageHeaderController.ts';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { getCapChartSecurities } from '@dealstack/frontend/app/services/CapTableData.service.ts';
import { CapHeaderContext } from '@dealstack/frontend/app/pages/Project/Cap/common/CapPageHeader/useCapPageHeaderController.tsx';
import { ChartSecuritiesTct, SecuritySummary } from '@dealstack/shared/lib/generated';
import { DealContext } from '@dealstack/frontend/app/pages/Project/DealDashboard/DealContext';

const flattenSecurities = (data?: ChartSecuritiesTct[]): SecuritySummary[] => {
	if (!data) {
		return [];
	}
	return data.flatMap((item) =>
		item.securities ? Object.values(item.securities).flat() : []
	) as SecuritySummary[];
};

export const useCapChartSecuritiesController = () => {
	const { dealId } = useContext(DealContext);
	const { filterDate } = useContext(CapHeaderContext);
	const [selectedSecurities, setSelectedSecurities] = useState<Map<string, boolean>>(new Map());
	const [appliedSecurityIds, setAppliedSecurityIds] = useState<string[] | undefined>(undefined);

	const {
		showSecuritiesDrawer,
		inSecuritiesMode,
		onToggleSpecifySecurities,
		onToggleSpecifySecuritiesDrawer,
		setShowSecuritiesDrawer,
	} = useCapChartSettingsController(dealId);

	const isoFilterDate = useMemo(() => filterDate?.toISOString(), [filterDate]);

	const {
		data: chartSecurities,
		isLoading: isLoadingSecurities,
		isSuccess: isSecuritiesSuccess,
	} = useQuery({
		queryKey: ['chartSecurities', dealId, isoFilterDate],
		queryFn: () => {
			if (!dealId) {
				return Promise.resolve([]);
			}
			return getCapChartSecurities({
				filterDate: isoFilterDate,
			});
		},
		enabled: !!dealId,
		staleTime: 5 * 60 * 1000,
		placeholderData: keepPreviousData,
	});

	const setAllSecuritiesSelected = useCallback(
		(data?: ChartSecuritiesTct[] | null) => {
			const securitiesList = data || chartSecurities || [];
			const newSelectedSecurities = new Map<string, boolean>();
			const allFlattenedSecurities = flattenSecurities(securitiesList);

			for (const security of allFlattenedSecurities) {
				if (security?.id) {
					newSelectedSecurities.set(security.id, true);
				}
			}
			setSelectedSecurities(newSelectedSecurities);
		},
		[chartSecurities]
	);

	useEffect(() => {
		if (isSecuritiesSuccess && chartSecurities) {
			setAllSecuritiesSelected(chartSecurities);
		}
	}, [isSecuritiesSuccess, chartSecurities, setAllSecuritiesSelected]);

	const initialSelectionDone = useRef(false);
	useEffect(() => {
		if (chartSecurities && !isLoadingSecurities && !initialSelectionDone.current) {
			setAllSecuritiesSelected(chartSecurities);
			initialSelectionDone.current = true;
		}
	}, [chartSecurities, isLoadingSecurities, setAllSecuritiesSelected]);

	const setSelectSecurities = useCallback((securityIds: string[], value: boolean) => {
		setSelectedSecurities((prev) => {
			const newSelectedSecurities = new Map(prev);
			for (const securityId of securityIds) {
				newSelectedSecurities.set(securityId, value);
			}
			return newSelectedSecurities;
		});
	}, []);

	const fetchWithSelectedSecuritiesView = useCallback(() => {
		const securityIdsToApply = Array.from(selectedSecurities.entries())
			.filter(([, isSelected]) => isSelected)
			.map(([id]) => id);

		if (JSON.stringify(securityIdsToApply) !== JSON.stringify(appliedSecurityIds)) {
			setAppliedSecurityIds(securityIdsToApply);
		} else if (appliedSecurityIds === undefined && securityIdsToApply.length > 0) {
			setAppliedSecurityIds(securityIdsToApply);
		} else {
			setShowSecuritiesDrawer?.(false);
		}
	}, [selectedSecurities, appliedSecurityIds, setShowSecuritiesDrawer]);

	const refetchDataWithResetSelectedSecurities = useCallback(() => {
		setAppliedSecurityIds(undefined);
		setAllSecuritiesSelected();
	}, [setAllSecuritiesSelected]);

	const handleResetSecuritiesView = useCallback(() => {
		refetchDataWithResetSelectedSecurities();
		onToggleSpecifySecurities(false);
	}, [refetchDataWithResetSelectedSecurities, onToggleSpecifySecurities]);

	return {
		dealId,
		isoFilterDate,
		chartSecurities,
		isLoadingSecurities,
		isSecuritiesSuccess,
		selectedSecurities,
		setSelectSecurities,
		setAllSecuritiesSelected,
		appliedSecurityIds,
		fetchWithSelectedSecuritiesView,
		refetchDataWithResetSelectedSecurities,
		handleResetSecuritiesView,
		showSecuritiesDrawer,
		inSecuritiesMode,
		onToggleSpecifySecurities,
		onToggleSpecifySecuritiesDrawer,
		setShowSecuritiesDrawer,
	};
};
